<template>

  <Dialog :visible="showDialog" class="license-popup" :modal="true" :draggable="false">
    <template #container>
      <div class="dialog-header flex-column sm:flex-row justify-content-between py-2">
        <div class="flex flex-row align-items-center gap-3">
          <img :alt="license.product.application" :src="license.product.icon_url" width="45" style="vertical-align: middle" />
          <span class="text-serial-number potentially-long-label" style="font-size: var(--popup-header-font-size); font-weight: var(--popup-header-font-weight);">{{ license.serial_number }}</span>
        </div>
        <div style="max-width: 65%;">
          <StatusTag class="potentially-long-label" :statusInformation="license.current_status" :pending="isPendingNow(license) ? license.pending : null" tooltipPosition="top" tagSize="big" :applicationName="license.product.application"/>
        </div>
      </div>
      
      <div class="dialog-content pt-2">
        <LicenseWindowDetails class="" :license="license"/>

        <div class="flex flex-column sm:flex-row gap-5 mt-4 justify-content-between" style="height: 50%;">
          <LicenseWindowHistory style="flex: 1;" :license="license"/>
          <LicenseWindowAuthorizedUsers style="flex: 1;" v-model:license="license" @close-license-window="emit('close')" @delegates-change-click="(val) => makeUpdateDelegatesRequest(val)" @refresh-licenses-table="emit('refresh-licenses-table')" @refresh-license="refreshLicense"/>
        </div>
      </div>

      <div class="dialog-footer align-items-center justify-content-between gap-1">
        <div class="flex flex-row" style="gap: 1rem;">
          <div v-if="license.current_status.activated" class="flex flex-row gap-3 align-items-center">
            <div v-if="isOldActivation(license)" class="flex flex-row" style="gap: 1rem;">
              <ButtonModule type="secondary" severity="danger" label="Force Release" @click="showConfirmationForceReleaseDialog = true" disabled v-tooltip.right="{ value: `You can not force release an activation running with the old activation system.`, autoHide: false }"/>
              <ButtonModule v-if="canBeDeleted" type="secondary" severity="danger" label="Remove Serial" @click="clickedDeleteSerial"/>
            </div>
            
            <div class="flex flex-row gap-3 align-items-center" v-else>
              <ButtonModule type="secondary" severity="danger" label="Force Release" @click="showConfirmationForceReleaseDialog = true" :disabled="isPendingNow(license)"/>
              <ButtonModule v-if="canBeDeleted" type="secondary" severity="danger" label="Remove Serial" @click="clickedDeleteSerial"/>
              <div v-if="pendingRemainingTimeLabel !== '-1' && pendingRemainingTimeLabel !== '...'" class="flex flex-row align-items-center">
                  <span style="font-size: var(--font-size-medium); font-weight: var(--font-weight-xthin);" v-if="license.pending">{{`There is a ${license.pending.pending_release ? 'release pending' : ''}${license.pending.pending_release && license.pending.pending_reactivate ? ' and a ' : ''}${license.pending.pending_reactivate ? 'recent reactivation' : ''} for that serial.`}} You can not force release currently. Please wait:
                  <span style="font-size: var(--font-size-medium); font-weight: var(--font-weight-normal);" v-if="license.pending">{{ (pendingRemainingTimeLabel && pendingRemainingTimeLabel !== '-1') ? pendingRemainingTimeLabel : '...' }}</span>
                </span>
              </div>
            </div>
          </div>
          <ButtonModule v-else-if="canBeDeleted" type="secondary" severity="danger" label="Remove Serial" @click="clickedDeleteSerial"/>
        </div>
        <div class="buttons" style="flex: 1;">
          <ButtonModule type="primary" severity="normal" label="Close" @click="emit('close')"/>
        </div>
      </div>
    </template>
  </Dialog>

  <!-- DIALOGS -->

  <!-- Force-Release license -->
  <ConfirmationForceReleaseDialog :showDialog="showConfirmationForceReleaseDialog" :license="license" @close="showConfirmationForceReleaseDialog = false" @force-release="(serialNumber, selectedReason, customizedReason) => confirmForceRelease(serialNumber, selectedReason, customizedReason)"/>

</template>

<script setup>
// --- IMPORTS ---
import { ref, toRefs, computed, watchEffect, onMounted, onBeforeUnmount } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog';
// vue
import ButtonModule from '@/components/Modules/ButtonModule';
import StatusTag from '@/components/Licenses/Elements/StatusTag.vue';
import LicenseWindowDetails from './Elements/LicenseWindowDetails.vue';
import LicenseWindowHistory from './Elements/LicenseWindowHistory.vue';
import LicenseWindowAuthorizedUsers from './Elements/LicenseWindowAuthorizedUsers.vue';
import ConfirmationForceReleaseDialog from '@/components/Dialogs/Actions/ConfirmationForceReleaseDialog.vue'
// Others
import toastService from '@/composables/toastService';
import UserUtils from '@/composables/UserUtils';
import LicensesUtils from '@/composables/LicensesUtils';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'

const store = useStore()
const router = useRouter()
const { logoutUser } = UserUtils(store, router)
const { isOldActivation, isPendingNow, getLicenseRemainingPendingTimeLabel } = LicensesUtils(store)

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchEditDelegates, fetchForceRelease, fetchTransferOwnership } = useApiRequest()
const httpEditDelegatesLoading = ref(false)
const httpEditDelegatesData = ref(null)
const httpForceReleaseLoading = ref(false)
const httpForceReleaseData = ref(null)
const httpTransferOwnershipLoading = ref(false)
const httpTransferOwnershipData = ref(null)

// --- PROPS ---
const props = defineProps({
  showDialog: Boolean,
  license: Object,
})
const { showDialog, license } = toRefs(props)

// --- User info ---
const user = computed(() => store.state.user.user)

// --- EMITS ---
const emit = defineEmits(['close', 'refresh-licenses-table', 'delete-serial-number'])

// --- VARIABLES ---

// Pending remaining time update
const pendingRemainingTimeLabel = ref("...")
let timeoutId;
onMounted(() => {
  function updatePendingRemainingTimeLabel() {
    if (showDialog.value && license.value && license.value.pending) {
      // Check which between the two pendings is the latest
      let expDate = new Date()
      if (license.value.pending.pending_release && license.value.pending.pending_release.exp_datetime) {
        expDate = expDate > license.value.pending.pending_release.exp_datetime ? expDate : license.value.pending.pending_release.exp_datetime
      }
      if (license.value.pending.pending_reactivate && license.value.pending.pending_reactivate.exp_datetime) {
        expDate = expDate > license.value.pending.pending_reactivate.exp_datetime ? expDate : license.value.pending.pending_reactivate.exp_datetime
      }
      pendingRemainingTimeLabel.value = getLicenseRemainingPendingTimeLabel(expDate)
    }
    // Execute this function again after 1 second
    timeoutId = setTimeout(updatePendingRemainingTimeLabel, 1000);
  }
  // Start the interval
  updatePendingRemainingTimeLabel();
})
watchEffect(() => {
  if (!showDialog.value) { pendingRemainingTimeLabel.value = "..." }
})
onBeforeUnmount(() => {
  // Clear the timeout when the component is unmounted
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
});

// - Delete Serial -
const canBeDeleted = computed(() => {
  if (isOldActivation(license.value)) { return true; } // If old activation: we can delete
  if (!license.value.current_status.activated) { return true; } // If it is not currently in use, we can delete
  if (license.value.admin.id == user.value.id) { return false; } // Therefore it is active, and if I am the owner, we can't delete
  if (license.value.current_status.activeUser && license.value.current_status.activeUser.id != user.value.id) { return true; } // Therefore it is active and I am not the admin, then if I am not the guy running the license, we can delete
  return false
})
const clickedDeleteSerial = () => {
    if (!isOldActivation(license.value) && (license.value.current_status.activated === true) && license.value.current_status.activeUser && license.value.current_status.activeUser.id == user.value.id) {
        toastService.add({ severity: 'error', summary: "Removal Error", detail: 'This serial number can not be removed because you are currently running the license.', life: 5000 });
    }
    else { // It is possible for me to remove !
      emit('delete-serial-number')
    }
}

// --- Confirmation of the force release ---
const showConfirmationForceReleaseDialog = ref(false)

// --- UPDATE DELEGATES ---
const makeUpdateDelegatesRequest = async (newDelegates) => {
  const newDelegatesId = newDelegates.map((user) => user.id)
  // Send HTTP request
  httpEditDelegatesLoading.value = true
    try {
        httpEditDelegatesData.value = await fetchEditDelegates({ licenses_modifications: [{ serial_number: license.value.serial_number, delegates: newDelegatesId }] })
        // Check update status
        const temp = httpEditDelegatesData.value.licenses_updated.filter((l) => l.serial_number === license.value.serial_number)
        if (temp.length === 0) {
          toastService.add({ severity: 'warn', summary: "Edit Authorized Users: Warning", detail: 'It seems that there is an issue refreshing the license details. Please try to refresh the page.', life: 5000 });
        }
        else {
          const updatedSerialInfo = temp[0]
          if (updatedSerialInfo.status !== "success") {
            toastService.add({ severity: updatedSerialInfo.status === 'warning' ? 'warn' : 'error', summary: `Edit Authorized Users: ${updatedSerialInfo.serial_number}`, detail: updatedSerialInfo.message, life: 5000 });
          }
          else {
            //toastService.add({ severity: 'success', summary: "Edit Authorized Users: Success", detail: 'The authorized users for the license have been successfully updated.', life: 5000 });
          }
          // IF THERE IS "delegates" in the license object, update delegates in the cache
          const updatedDelegates = updatedSerialInfo["delegates"]
          if (updatedDelegates) {
            store.commit('licenses/updateLicenseDelegates', { serial_number: updatedSerialInfo.serial_number, delegates: updatedDelegates })
            // Load locally the updated license info
            let temp = store.state.licenses.licenses.filter((l) => l.serial_number === license.value.serial_number)
            if (temp.length>0) {
              license.value.delegates = temp[0].delegates
            }
          }
          // Close
          //emit('close')
        }
    } catch (err) {
        handleEditDelegatesError(err)
    } finally {
      httpEditDelegatesLoading.value = false
    }
}
// Handle an error from the server
const handleEditDelegatesError = (error) => {
    try {  
      if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
          toastService.add({ severity: 'error', summary: `Edit Authorized Users: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
          return;
      }
      const errorStatus = error.response.status
      const errorCode = error.response.data.error_code
      const errorMessage = error.response.data.error_message
      switch (errorStatus) {
          case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM (400)
              if (errorCode == 'ACCESS_TOKEN_MISSING') {
                  toastService.add({ severity: 'error', summary: "Edit Authorized Users: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                  // Logout
                  logoutUser()
              }
              else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                  toastService.add({ severity: 'warn', summary: "Edit Authorized Users: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
              }
              else { toastService.add({ severity: 'error', summary: `Edit Authorized Users: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID (401)
              if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                  // Logout: Do nothing, the interceptor should have made a refresh token request!
                  // logoutUser()
              }
              else { toastService.add({ severity: 'error', summary: `Edit Authorized Users: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 404: // ACCOUNT_NOT_FOUND (404) => toast + logout
              if (errorCode == 'ACCOUNT_NOT_FOUND') { // ACCOUNT_NOT_FOUND
                  toastService.add({ severity: 'error', summary: "Edit Authorized Users: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                  // Logout
                  logoutUser()
              }
              else { toastService.add({ severity: 'error', summary: `Edit Authorized Users: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast
              if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                  toastService.add({ severity: 'error', summary: "Edit Authorized Users: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
              }
              else { toastService.add({ severity: 'error', summary: `Edit Authorized Users: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          default:
              toastService.add({ severity: 'error', summary: `Edit Authorized Users: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
              break;
      }
    } catch {
        toastService.add({ severity: 'error', summary: `Edit Authorized Users: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- SUBMIT FORCE RELEASE CONFIRMED ---
const confirmForceRelease = async (serialNumber, selectedReason, customizedReason ) => {
    // Make sure the requested serial number to be force released corresponds to the one we are currently "processing" in the license window... (this should never fail...)
    if (serialNumber != license.value.serial_number) {
        toastService.add({ severity: 'error', summary: "Force Release: Unexpected Issue.", detail: 'Please refresh the page and retry. If the issue persists, please contact Softron Support.', life: 5000 });
        return
    }
    // Send HTTP request with passwordVerification
    httpForceReleaseLoading.value = true
    try {
        let requestInfo = { selected_reason: selectedReason, license_serial_number: license.value.serial_number }
        if (selectedReason == "ANOTHER_REASON") { requestInfo = { ...requestInfo, customized_reason: customizedReason } }
        httpForceReleaseData.value = await fetchForceRelease(requestInfo)
        // Reset of values
        showConfirmationForceReleaseDialog.value = false
        // Refresh of data
        store.commit('licenses/updateLicenseHistoryAndPendingInfo', httpForceReleaseData.value.license_updated)
        // Success toast
        //toastService.add({ severity: 'success', summary: "Force Release: Success", detail: 'The license has been successfully force released.', life: 5000 });
        // Load locally the updated license info
        let temp = store.state.licenses.licenses.filter((l) => l.serial_number === license.value.serial_number)
        if (temp.length>0) {
          license.value.history = temp[0].history
          license.value.current_status = temp[0].current_status
          if (temp[0].pending) { license.value.pending = temp[0].pending }
        }
        // Close
        //emit('close')
    } catch (err) {
        handleForceReleaseError(err)
    } finally {
      httpForceReleaseLoading.value = false
    }
}
// Handle an error from the server
const handleForceReleaseError = (error) => {
    try {  
      if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
          toastService.add({ severity: 'error', summary: `Force Release: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
          return;
      }
      const errorStatus = error.response.status
      const errorCode = error.response.data.error_code
      const errorMessage = error.response.data.error_message
      switch (errorStatus) {
          case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM (400)
              if (errorCode == 'ACCESS_TOKEN_MISSING') {
                  toastService.add({ severity: 'error', summary: "Force Release: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                  // Logout
                  logoutUser()
              }
              else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                  toastService.add({ severity: 'warn', summary: "Force Release: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
              }
              else { toastService.add({ severity: 'error', summary: `Force Release: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 401: // USER_NOT_OWNER_NOT_DELEGATE / LICENSE_NOT_IN_USE / ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID (401)
              if (errorCode == 'USER_NOT_OWNER_NOT_DELEGATE') { // USER_NOT_OWNER_NOT_DELEGATE
                  toastService.add({ severity: 'error', summary: "Force Release: Not Authorized.", detail: errorMessage, life: 5000 });
                  emit('refresh-licenses-table')
                  emit('close')
              }
              else if (errorCode == 'LICENSE_NOT_IN_USE') { // LICENSE_NOT_IN_USE
                  toastService.add({ severity: 'warn', summary: "Force Release: License Not Running.", detail: errorMessage, life: 5000 });
                  emit('refresh-licenses-table')
                  emit('close')
              }
              else if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                  // Logout: Do nothing, the interceptor should have made a refresh token request!
                  // logoutUser()
              }
              else { toastService.add({ severity: 'error', summary: `Force Release: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 403: // FORCE_RELEASE_NOT_AUTHORIZED (401)
              if (errorCode == 'FORCE_RELEASE_NOT_AUTHORIZED') { // FORCE_RELEASE_NOT_AUTHORIZED
                  toastService.add({ severity: 'error', summary: "Force Release: Can't Release.", detail: errorMessage, life: 5000 });
                  emit('refresh-licenses-table')
                  emit('close')
              }
              else { toastService.add({ severity: 'error', summary: `Force Release: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 404: // ACCOUNT_NOT_FOUND / SERIAL_INVALID / NOT_FOUND (404)
              if (errorCode == 'SERIAL_INVALID') { // SERIAL_INVALID
                  toastService.add({ severity: 'error', summary: "Force Release: Invalid Serial Number.", detail: 'The license is invalid and has been deleted from the table.', life: 5000 });
                  store.commit('licenses/removeLicense', license.value.serial_number)
                  emit('refresh-licenses-table')
                  emit('close')
              }
              else if (errorCode == 'ACCOUNT_NOT_FOUND') { // ACCOUNT_NOT_FOUND
                  toastService.add({ severity: 'error', summary: "Force Release: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                  // Logout
                  logoutUser()
              }
              else { toastService.add({ severity: 'error', summary: `Force Release: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast
              if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') { // DATABASE_ERROR / INTERNAL_ERROR
                  toastService.add({ severity: 'error', summary: "Force Release: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
              }
              else { toastService.add({ severity: 'error', summary: `Force Release: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          default:
              toastService.add({ severity: 'error', summary: `Force Release: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
              break;
      }
    } catch {
        toastService.add({ severity: 'error', summary: `Force Release: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- TRANSFER OWNERSHIP ---
const refreshLicense = () => {
  let temp = store.state.licenses.licenses.filter((l) => l.serial_number === license.value.serial_number)
  if (temp.length>0) {
    license.value.history = temp[0].history
    license.value.current_status = temp[0].current_status
    license.value.admin = temp[0].admin
    license.value.delegates = temp[0].delegates
    if (temp[0].pending) { license.value.pending = temp[0].pending }
  }
}

const makeTransferOwnershipRequest = async (serialNumber, newOwnerId) => {
  if (serialNumber != license.value.serial_number) {
    toastService.add({ severity: 'error', summary: `Transfer Ownership: Error.`, detail: "It seems that there is an issue transfering the ownership. Please refresh the page and try again.", life: 5000 });
    return
  }
  // Send HTTP request
  httpTransferOwnershipLoading.value = true
    try {
        httpTransferOwnershipData.value = await fetchTransferOwnership({ serial_number: serialNumber, new_owner_id: newOwnerId })
        console.log("result = ", httpTransferOwnershipData.value.license)
        // Refresh of data
        store.dispatch('licenses/updateLicense', httpTransferOwnershipData.value.license)
        console.log("license: ", license.value)
        console.log("updated license: ", httpTransferOwnershipData.value.license)
        license.value = httpTransferOwnershipData.value.license
        console.log("license: ", license.value)


        
        let temp = store.state.licenses.licenses.filter((l) => l.serial_number === license.value.serial_number)
        if (temp.length>0) {
          license.value.history = temp[0].history
          license.value.current_status = temp[0].current_status
          license.value.admin = temp[0].admin
          license.value.delegates = temp[0].delegates
          if (temp[0].pending) { license.value.pending = temp[0].pending }
        }
    } catch (err) {
      handleTransferOwnershipError(err)
    } finally {
      httpTransferOwnershipLoading.value = false
    }
}
// Handle an error from the server
const handleTransferOwnershipError = (error) => {
    try {  
      if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
          toastService.add({ severity: 'error', summary: `Transfer Ownership: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
          return;
      }
      const errorStatus = error.response.status
      const errorCode = error.response.data.error_code
      const errorMessage = error.response.data.error_message
      switch (errorStatus) {
        // 400: MISSING_OBJ / ACCESS_TOKEN_MISSING / MISSING_OBJ_ELEM
        // 401: ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
        // 404: ACCOUNT_NOT_FOUND / SERIAL_INVALID
        // 409: USER_NOT_OWNER / NEW_OWNER_ACCOUNT_NOT_FOUND
        // 500: DATABASE_ERROR / INTERNAL_ERROR
          case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM (400)
              if (errorCode == 'ACCESS_TOKEN_MISSING') {
                  toastService.add({ severity: 'error', summary: "Transfer Ownership: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                  // Logout
                  logoutUser()
              }
              else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                  toastService.add({ severity: 'warn', summary: "Transfer Ownership: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
              }
              else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID (401)
              if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                  // Logout: Do nothing, the interceptor should have made a refresh token request!
                  // logoutUser()
              }
              else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 404: // SERIAL_INVALID / ACCOUNT_NOT_FOUND / NEW_OWNER_ACCOUNT_NOT_FOUND (404) => toast + logout
              if (errorCode == 'SERIAL_INVALID') { // SERIAL_INVALID
                  toastService.add({ severity: 'error', summary: "Transfer Ownership: Invalid Serial Number.", detail: 'The license is invalid and has been deleted from the table.', life: 5000 });
                  store.commit('licenses/removeLicense', license.value.serial_number)
                  emit('refresh-licenses-table')
                  emit('close')
              }
              else if (errorCode == 'ACCOUNT_NOT_FOUND') { // ACCOUNT_NOT_FOUND
                  toastService.add({ severity: 'error', summary: "Transfer Ownership: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                  // Logout
                  logoutUser()
              }
              else if (errorCode == 'NEW_OWNER_ACCOUNT_NOT_FOUND') { // NEW_OWNER_ACCOUNT_NOT_FOUND
                  toastService.add({ severity: 'error', summary: "Transfer Ownership: Other User Not Found.", detail: 'The account of the user you want to transfer to does not exist. Please refresh and try again.', life: 8000 });
              }
              else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 409: // USER_NOT_OWNER (409)
              if (errorCode == 'USER_NOT_OWNER') { // USER_NOT_OWNER
                  toastService.add({ severity: 'error', summary: "Transfer Ownership: Not Authorized.", detail: errorMessage, life: 5000 });
                  emit('refresh-licenses-table')
                  emit('close')
              }
              else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast
              if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                  toastService.add({ severity: 'error', summary: "Transfer Ownership: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
              }
              else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
              break;
          default:
              toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
              break;
      }
    } catch {
        toastService.add({ severity: 'error', summary: `Transfer Ownership: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}
</script>
<style scoped>
:deep(.p-chips .p-chips-multiple-container .p-chips-token) {
  margin-top: 0.25rem;
}
</style>